import { Show, type Component } from 'solid-js';

import type { GameStore } from '../state/store';
import { ActionBar } from './ActionBar';
import { StatusEffectRow } from './StatusEffectRow';
import { ApocalypseBanner } from './ApocalypseBanner';
import { FloatingDamageOverlay } from './FloatingDamageOverlay';
import { HurtFlash } from './HurtFlash';
import { FilterEditor } from './FilterEditor';
import { GrindPortal, LootPrompt } from './GrindPortal';
import { LootLabels } from './LootLabels';
import { CastBar } from './CastBar';
import { Minimap } from './Minimap';
import { Nameplates, TargetFrame } from './Nameplates';
import { WorldMarkers } from './WorldMarkers';
import { RebirthModal } from './RebirthModal';
import { CharacterSheet } from './CharacterSheet';
import { HudMenu } from './HudMenu';
import { SkillsPanel } from './SkillsPanel';
import { PassivesPanel } from './PassivesPanel';
import { ChatBox } from './ChatBox';
import { LevelUpOverlay } from './LevelUpOverlay';
import { PartyBar } from './PartyBar';
import { SocialPanel } from './SocialPanel';
import { TownBar } from './TownBar';
import { StationPanel } from './StationPanel';
import { TestModeBar, TestPanel } from './TestPanel';
import { SystemMenu } from './SystemMenu';
import { BagPanel } from './BagPanel';
import { DragLayer } from './DragLayer';
import { DragProvider } from './DragContext';

/**
 * The overlay root.
 *
 * This element must stay `pointer-events: none` (see styles/base.css). Clicks on
 * empty HUD space have to reach the canvas underneath, or half the map becomes
 * unwalkable. Interactive children opt back in with `.hud-interactive`.
 *
 * Order here is paint order: world-anchored elements first, then the bar, then
 * banners, then panels, then the modal — matching the z-index scale in
 * styles/tokens.css.
 */
export const Hud: Component<{
  store: GameStore;
  /** Passed to `GrindPortal`; see its own note on why walking is not the HUD's. */
  onWalkToGate?: (gate: { x: number; z: number }) => void;
}> = (props) => (
  // One drag controller for the whole overlay, because a drag crosses panels: the grid
  // and the paper doll are two components, and the bag and the vault two more. Two
  // controllers would mean an item picked up in one simply did not exist in the other.
  <DragProvider store={props.store}>
  <div class="hud" data-testid="hud">
    <div class="atmosphere" />
    {/* Over the world and under everything that carries information: a wash that
        painted over a nameplate would be hiding the thing it is warning about. */}
    <HurtFlash store={props.store} />

      <WorldMarkers store={props.store} />
    <Nameplates store={props.store} />
    <FloatingDamageOverlay store={props.store} />

    {/* The filter's labels sit above the nameplates and below the loot prompt: a
        label names what is on the floor, the prompt names the one you are next to. */}
    <LootLabels store={props.store} />
    <LootPrompt store={props.store} />

    {/* Top-right, which is the one corner nothing else claims: the developer bar is
        top-left, the workshops and the portal button are top-centre, and the panel bar
        and orbs are along the bottom. Measured in a browser rather than assumed — a HUD
        box that overlaps another swallows its clicks, and jsdom cannot see it. */}
    <Minimap store={props.store} />
    {/* Beside the minimap rather than in the panel rail. It is not a panel — it exists
        only in a league that permits developer commands, so a slot for it in the rail is a
        gap in most sessions. Top-right keeps the two "instrument" widgets together. */}
    <TestModeBar store={props.store} />

    <TargetFrame store={props.store} />
    <StatusEffectRow store={props.store} />
    {/* Centre screen, below the character: see CastBar for why that band. */}
    <CastBar store={props.store} />
    <ActionBar store={props.store} />
    <GrindPortal store={props.store} onWalkToGate={props.onWalkToGate} />
    <TownBar store={props.store} onWalkToGate={props.onWalkToGate} />
    <LevelUpOverlay store={props.store} />
    {/* Left edge: the only side not already taken. The minimap and the test button are
        top-right, the rail is right, the orbs and action bar bottom-centre, the town bar
        top-centre. */}
    <PartyBar store={props.store} />
    <ChatBox store={props.store} />
    {/* Top-left, and only when the league permits a developer command. That corner is
        empty: every other interactive box is at the top centre, bottom centre or
        bottom-right, and overlapping one would swallow its clicks. */}

    <ApocalypseBanner store={props.store} />
    {/*
      **Two different messages, because they are two different situations.**

      A dropped network reconnects on a backoff, and *Reconnecting* is a true statement about
      what is happening. A **displaced** socket never dials back — the server replaced it with
      a newer connection for the same character — so showing the same banner would promise
      something the client has no intention of doing, and it flapped once a second while two
      tabs fought over one character.
    */}
    <Show when={props.store.displaced()}>
      {/*
        **A blocking scrim, not a banner.** There is nothing to do underneath: the socket
        will not reconnect, so every click and key would be a command going nowhere. Letting
        the player keep trying to move is the cruel version of telling them nothing.

        `hud-interactive` so it takes the pointer — see the pass-through rule in `base.css`.
      */}
      <div class="displaced hud-interactive" data-testid="displaced-banner" role="alertdialog">
        <div class="plate plate--riveted displaced__panel">
          <h2 class="displaced__title">Played elsewhere</h2>
          <p class="displaced__body">
            This character is being played in another window.
          </p>
          <button
            type="button"
            class="sigil-button displaced__action"
            data-testid="displaced-reload"
            onClick={() => location.reload()}
          >
            <span>Take it back</span>
          </button>
        </div>
      </div>
    </Show>
    <Show when={!props.store.connected() && !props.store.displaced()}>
      <div class="connection-banner" data-testid="connection-banner">
        Reconnecting
      </div>
    </Show>

    {/*
      **The rail: every panel button in one column down the right edge.**

      They were a horizontal strip along the bottom and the developer button was alone in
      the top-left corner — two places to look for the same kind of control, and the strip
      grew wider every time a panel was added. A column grows *downward*, which is the
      direction there is room in.

      One container rather than two absolutely-positioned stacks, so they cannot overlap
      each other however many buttons either gains. It starts below the minimap and ends
      above the action bar; both are measured, because jsdom computes no layout and a HUD
      box that overlaps another swallows its clicks.
    */}
    <div class="hud-rail">
      <HudMenu store={props.store} />
    </div>
    <BagPanel store={props.store} />
    <CharacterSheet store={props.store} />
    <SkillsPanel store={props.store} />
    <PassivesPanel store={props.store} />
    <SocialPanel store={props.store} />
    <FilterEditor store={props.store} />
    <StationPanel store={props.store} />
    <TestPanel store={props.store} />
    <SystemMenu store={props.store} />
    <RebirthModal store={props.store} />

    {/* Last, so the ghost paints over every panel it might be dropped on — and so the
        window-level "released over nothing" listener exists whenever the HUD does. */}
    <DragLayer />
  </div>
  </DragProvider>
);
